import { useState, useEffect } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import { Builder } from '@builder.io/react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { BsPin } from 'react-icons/bs';
import { BiLocationPlus } from 'react-icons/bi';
import { HiLocationMarker, HiOutlineLocationMarker } from 'react-icons/hi';

// Autoplay plugin
function AutoplayPlugin(slider) {
    let timeout;
    let mouseOver = false;

    const clearNextTimeout = () => clearTimeout(timeout);

    const nextTimeout = () => {
        clearTimeout(timeout);
        if (mouseOver) return;
        timeout = setTimeout(() => slider.next(), 3000);
    };

    slider.on("created", () => {
        slider.container.addEventListener("mouseover", () => {
            mouseOver = true;
            clearNextTimeout();
        });
        slider.container.addEventListener("mouseout", () => {
            mouseOver = false;
            nextTimeout();
        });
        nextTimeout();
    });

    slider.on("dragStarted", clearNextTimeout);
    slider.on("animationEnded", nextTimeout);
    slider.on("updated", nextTimeout);
}

export const WeekendDistributionCarousel = ({
    items = [],
    loop = true,
    spacing = 20,
    perView = { desktop: 4, tablet: 2, mobile: 1 },
    imageMaxHeight = 300,
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const getPerView = () => {
        if (typeof window === "undefined") return perView.desktop;
        if (window.innerWidth < 640) return perView.mobile || 1;
        if (window.innerWidth < 1024) return perView.tablet || 2;
        return perView.desktop || 3;
    };

    const [perViewValue, setPerViewValue] = useState(getPerView());

    useEffect(() => {
        const handleResize = () => setPerViewValue(getPerView());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const useSlider = items.length > perViewValue;

    const [sliderRef, instanceRef] = useKeenSlider(
        {
            mode: 'snap',
            loop,
            slideChanged: (s) => setCurrentSlide(s.track.details.rel),
            created: () => setLoaded(true),
            slides: {
                perView: perViewValue,
                spacing,
            },
        },
        useSlider ? [AutoplayPlugin] : []
    );

    return (
        <section className="flex flex-col">
            <div className="relative w-full">
                {useSlider ? (
                    <>
                        <div ref={sliderRef} className="keen-slider w-full">
                            {items.map(({ img, caption, date, location, link, openInNewTab }, idx) => (
                                <div
                                    className="keen-slider__slide flex flex-col items-center"
                                    key={idx}
                                >
                                    <div className="relative">
                                        {link ? (
                                            <a href={link} target={openInNewTab ? "_blank" : "_self"} rel="noopener noreferrer">
                                                <img
                                                    src={img?.src}
                                                    alt={img?.alt || 'Aid distribution image'}
                                                    style={{ maxHeight: `${imageMaxHeight}px`, width: 'auto' }}
                                                />
                                            </a>
                                        ) : (
                                            <img
                                                src={img?.src}
                                                alt={img?.alt || 'Aid distribution image'}
                                                style={{ maxHeight: `${imageMaxHeight}px`, width: 'auto' }}
                                            />
                                        )}
                                        <div className="flex flex-col gap-3 p-2">
                                            {(date || location) && (
                                                <div className="flex flex-col gap-1 font-montserrat font-medium text-sm italic text-[#212121]">
                                                    {date && <span className='flex items-center gap-2'><CalendarDaysIcon className='text-sm w-[14px]' />{date}</span>}
                                                    {location && <span className='flex items-center gap-2'><HiOutlineLocationMarker className='text-sm w-[14px]' />{location}</span>}
                                                </div>
                                            )}
                                            {caption && (
                                                <div className="font-montserrat leading-[22px] text-sm text-[#696969]">
                                                    {caption && <span>{caption}</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {loaded && instanceRef.current && (
                            <div className="flex justify-center mt-4 gap-2">
                                {items.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => instanceRef.current?.moveToIdx(idx)}
                                        className={`w-3 h-3 rounded-full border-2 transition-all duration-300 ${currentSlide === idx ? 'bg-[#253B7E] border-[#253B7E]' : 'bg-white border-gray-300'
                                            }`}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-wrap gap-4 justify-center">
                        {items.map(({ img, caption, date, location, link, openInNewTab }, idx) => (
                            <div key={idx} className="flex flex-col items-center max-w-xs">
                                {link ? (
                                    <a href={link} target={openInNewTab ? "_blank" : "_self"} rel="noopener noreferrer">
                                        <img
                                            src={img?.src}
                                            alt={img?.alt || 'Aid image'}
                                            style={{ maxHeight: `${imageMaxHeight}px`, width: 'auto' }}
                                        />
                                    </a>
                                ) : (
                                    <img
                                        src={img?.src}
                                        alt={img?.alt || 'Aid image'}
                                        style={{ maxHeight: `${imageMaxHeight}px`, width: 'auto' }}
                                    />
                                )}
                                <div className="flex flex-col gap-2 p-2">
                                    {(date || location) && (
                                        <div className="flex flex-col gap-1 font-montserrat font-medium text-[#212121]">
                                            {date && <span>{date}</span>}
                                            {location && <span>{location}</span>}
                                        </div>
                                    )}
                                    {caption && (
                                        <div className="font-montserrat leading-[22px] text-sm text-[#696969]">
                                            {caption && <span>{caption}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

Builder.registerComponent(WeekendDistributionCarousel, {
    name: 'WeekendDistributionCarousel',
    inputs: [
        {
            name: 'items',
            type: 'list',
            subFields: [
                {
                    name: 'img',
                    type: 'object',
                    subFields: [
                        { name: 'src', type: 'file', allowedFileTypes: ['jpeg', 'png', 'svg'] },
                        { name: 'alt', type: 'string' },
                    ],
                    required: true,
                },
                { name: 'caption', type: 'string', helperText: 'Optional short caption.' },
                { name: 'date', type: 'string', helperText: 'Date of the distribution (e.g., July 28)' },
                { name: 'location', type: 'string', helperText: 'Location name (e.g., Khan Yunis)' },
                { name: 'link', type: 'string' },
                { name: 'openInNewTab', type: 'boolean', defaultValue: false },
            ],
        },
        { name: 'loop', type: 'boolean', defaultValue: true },
        { name: 'spacing', type: 'number', defaultValue: 20 },
        {
            name: 'perView',
            type: 'object',
            subFields: [
                { name: 'desktop', type: 'number', defaultValue: 4 },
                { name: 'tablet', type: 'number', defaultValue: 2 },
                { name: 'mobile', type: 'number', defaultValue: 1 },
            ],
        },
        { name: 'imageMaxHeight', type: 'number', defaultValue: 300 },
        { name: 'showCaptions', type: 'boolean', defaultValue: true },
        {
            name: 'captionPosition',
            type: 'string',
            enum: ['bottom', 'overlay'],
            defaultValue: 'bottom',
        },
    ],
});
